/**
 * USB status-light boundary (presentation output subscriber).
 *
 * Fail-open if the lamp is absent. Never participates in policy correctness.
 * Color is driven by presentation phase only (CLEAR/OBSERVING/CONDITION ACTIVE).
 */

export const USB_LIGHT_OUTPUT_REASON = 'presentation_output';
export const USB_LIGHT_WAIT_REASON = USB_LIGHT_OUTPUT_REASON;

export type DwellPhase = 'clear' | 'observing' | 'active' | 'unknown';
export type UsbLightColor = 'green' | 'yellow' | 'red' | 'off';

export type UsbLampTestResult = {
  ok: boolean;
  connected: boolean;
  sequence: UsbLightColor[];
  reason: string;
};

export type UsbLightApplyResult = {
  ok: boolean;
  connected: boolean;
  color: UsbLightColor;
  reason: string;
};

export type UsbLightAdapter = {
  connected: boolean;
  applyPhase(phase: DwellPhase): Promise<UsbLightApplyResult>;
  lampTest(): Promise<UsbLampTestResult>;
};

export function mapDwellPhaseToLight(phase: DwellPhase): UsbLightColor {
  if (phase === 'clear') return 'green';
  if (phase === 'observing') return 'yellow';
  if (phase === 'active') return 'red';
  return 'off';
}

export const LAMP_TEST_SEQUENCE: UsbLightColor[] = ['green', 'yellow', 'red', 'off'];

export function createFailOpenUsbLightAdapter(args?: { connected?: boolean }): UsbLightAdapter {
  const connected = Boolean(args?.connected);
  return {
    connected,
    async applyPhase(phase: DwellPhase): Promise<UsbLightApplyResult> {
      const color = mapDwellPhaseToLight(phase);
      if (!connected) {
        return {
          ok: true,
          connected: false,
          color: 'off',
          reason: 'light_not_connected',
        };
      }
        return {
          ok: true,
          connected: true,
          color,
          reason: USB_LIGHT_OUTPUT_REASON,
        };
    },
    async lampTest(): Promise<UsbLampTestResult> {
      if (!connected) {
        return {
          ok: true,
          connected: false,
          sequence: [],
          reason: 'light_not_connected',
        };
      }
      return {
        ok: true,
        connected: true,
        sequence: [...LAMP_TEST_SEQUENCE],
        reason: 'lamp_test',
      };
    },
  };
}
